import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import DataTable from "../interfaccia/DataTableBase";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

function formatDate(value) {
  if (!value) {
    return "";
  }
  return String(value).slice(0, 10);
}

function ControlliCorrispettivi({ token }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState("");
  const [filtroTesto, setFiltroTesto] = useState("");
  const [filtroPiattaforma, setFiltroPiattaforma] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroPeriodoDa, setFiltroPeriodoDa] = useState("");
  const [filtroPeriodoA, setFiltroPeriodoA] = useState("");

  const loadFiles = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setErrore("");
    try {
      const response = await axios.get(
        `${apiUrl}/contab/controlli-corrispettivi/files`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            q: filtroTesto || undefined,
            piattaforma: filtroPiattaforma || undefined,
            stato: filtroStato || undefined,
            periodoDa: filtroPeriodoDa || undefined,
            periodoA: filtroPeriodoA || undefined,
          },
        },
      );
      setFiles(response.data.data || []);
    } catch (error) {
      setErrore(
        error.response?.data?.error ||
          error.message ||
          "Errore caricamento controlli corrispettivi",
      );
    } finally {
      setLoading(false);
    }
  }, [
    token,
    filtroTesto,
    filtroPiattaforma,
    filtroStato,
    filtroPeriodoDa,
    filtroPeriodoA,
  ]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const piattaformeDisponibili = useMemo(
    () =>
      Array.from(
        new Set(files.map((file) => file.piattaforma).filter(Boolean)),
      ).sort(),
    [files],
  );

  const statiDisponibili = useMemo(
    () =>
      Array.from(
        new Set(files.map((file) => file.stato).filter(Boolean)),
      ).sort(),
    [files],
  );

  const filteredFiles = useMemo(() => {
    const testo = filtroTesto.trim().toLowerCase();

    return files.filter((file) => {
      const periodoFileDa = formatDate(file.periodo_da);
      const periodoFileA = formatDate(file.periodo_a || file.periodo_da);
      const matchPeriodoDa =
        !filtroPeriodoDa || (periodoFileA && periodoFileA >= filtroPeriodoDa);
      const matchPeriodoA =
        !filtroPeriodoA || (periodoFileDa && periodoFileDa <= filtroPeriodoA);
      const matchTesto =
        !testo ||
        [file.nome_file_originale, file.account, file.tipo_report]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(testo));
      const matchPiattaforma =
        !filtroPiattaforma || file.piattaforma === filtroPiattaforma;
      const matchStato = !filtroStato || file.stato === filtroStato;

      return (
        matchTesto &&
        matchPiattaforma &&
        matchStato &&
        matchPeriodoDa &&
        matchPeriodoA
      );
    });
  }, [
    files,
    filtroTesto,
    filtroPiattaforma,
    filtroStato,
    filtroPeriodoDa,
    filtroPeriodoA,
  ]);

  const resetFiltri = () => {
    setFiltroTesto("");
    setFiltroPiattaforma("");
    setFiltroStato("");
    setFiltroPeriodoDa("");
    setFiltroPeriodoA("");
  };

  const columns = useMemo(
    () => [
      {
        name: "Files",
        selector: (row) => row.nome_file_originale,
        sortable: true,
        width: "350px",
      },
      {
        name: "Piattaforma",
        selector: (row) => row.piattaforma,
        sortable: true,
        width: "130px",
      },
      {
        name: "Account",
        selector: (row) => row.account,
        sortable: true,
        width: "130px",
      },
      {
        name: "Report",
        selector: (row) => row.tipo_report,
        sortable: true,
        width: "190px",
      },
      {
        name: "Periodo",
        width: "170px",
        cell: (row) => (
          <span>
            {formatDate(row.periodo_da)} {row.periodo_a && "-"}{" "}
            {formatDate(row.periodo_a)}
          </span>
        ),
      },
      {
        name: "Stato",
        selector: (row) => row.stato,
        sortable: true,
        width: "120px",
      },
      {
        name: "Registrazione",
        width: "350px",
        cell: (row) => (
          <span>
            <span className="tastoArancio br2 ph2 pv1 mr1">
              Attesa {row.righe_in_attesa_conferma || 0}
            </span>
            <span className="tastoVerde white br2 ph2 pv1 mr1">
              Confermate {row.righe_confermate || 0}
            </span>
            <span className="sfondoColScuro white br2 ph2 pv1">
              Ignorate {row.righe_da_ignorare || 0}
            </span>
          </span>
        ),
      },
      {
        name: "Esiti",
        width: "190px",
        cell: (row) => (
          <span>
            OK {row.righe_ok || 0} - MAN {row.righe_manuali || 0} - KO{" "}
            {row.righe_ko || 0}
          </span>
        ),
      },
      {
        name: "Creato",
        selector: (row) => row.created_at,
        sortable: true,
        width: "140px",
        cell: (row) => formatDate(row.created_at),
      },
      {
        name: "",
        width: "90px",
        cell: (row) => {
          const routeByPiattaforma = {
            AMAZON: "controlloCorrispettiviAmazon",
            PRESTASHOP: "controlloCorrispettiviPrestashop",
            GESTIONALE: "controlloCorrispettiviGestionale",
            DOMINO: "controlloCorrispettiviGestionale",
          };
          const route = routeByPiattaforma[row.piattaforma];

          return route ? (
            <Link
              className={stilePulsanti.buttonClassesWhite}
              to={`/${route}?fileId=${row.id}`}
            >
              <span class="material-symbols-outlined">folder_open</span>
            </Link>
          ) : (
            <span className="colScrittaDef-20">Apri</span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="sfondoBiancoTema pa3">
      <div className="flex flex-wrap items-center justify-between bb bordoColAlt pb3 mb3">
        <div>
          <div className="f4 b">Controlli corrispettivi</div>
          <div className="f6 colScrittaDef-20">
            File caricati e stato conferme righe
          </div>
        </div>
        <div className="flex flex-wrap items-center">
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/controlloCorrispettiviAmazon"
          >
            Nuovo Amazon IVA
          </Link>
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/controlloCorrispettiviPrestashop"
          >
            Nuovo Prestashop
          </Link>
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/controlloCorrispettiviGestionale"
          >
            Nuovo Gestionale
          </Link>
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/transazioniDominoCorrispettivi"
          >
            Transazioni Domino
          </Link>
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/riepilogoCorrispettiviConfermati"
          >
            Riepilogo confermati
          </Link>

          <button
            className={stilePulsanti.buttonClassesWhite}
            type="button"
            onClick={loadFiles}
          >
            Aggiorna
          </button>
        </div>
      </div>

      {errore && <div className="tastoRosso white pa3 br2 mb3">{errore}</div>}

      <div className="formRicercaOrizzontale pa2 flex flex-wrap justify-between items-center mb3">
        <div className="flex flex-wrap items-center">
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="text"
              placeholder="File o account"
              value={filtroTesto}
              onChange={(event) => setFiltroTesto(event.target.value)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroPiattaforma}
              onChange={(event) => setFiltroPiattaforma(event.target.value)}
            >
              <option value="">Piattaforma: tutte</option>
              {piattaformeDisponibili.map((piattaforma) => (
                <option key={piattaforma} value={piattaforma}>
                  {piattaforma}
                </option>
              ))}
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroStato}
              onChange={(event) => setFiltroStato(event.target.value)}
            >
              <option value="">Stato: tutti</option>
              {statiDisponibili.map((stato) => (
                <option key={stato} value={stato}>
                  {stato}
                </option>
              ))}
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="date"
              title="Periodo da"
              value={filtroPeriodoDa}
              onChange={(event) => setFiltroPeriodoDa(event.target.value)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="date"
              title="Periodo a"
              value={filtroPeriodoA}
              onChange={(event) => setFiltroPeriodoA(event.target.value)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <button
              className="dim tastoInputOrizzontale br3"
              type="button"
              onClick={resetFiltri}
            >
              RESET
            </button>
          </div>
        </div>
        <div className="colScrittaDef-20 mb2">
          File {filteredFiles.length}/{files.length}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredFiles}
        progressPending={loading}
        pagination
        paginationPerPage={50}
        paginationRowsPerPageOptions={[25, 50, 100, 200]}
        dense
        highlightOnHover
      />
    </div>
  );
}

export default ControlliCorrispettivi;
